import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Radio,
  message,
  Upload,
  Image,
  Divider,
} from 'antd';
import locale from 'antd/es/date-picker/locale/zh_CN';
import type { UploadFile } from 'antd/es/upload/interface';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { request } from '@/fetch/core';
import { Method } from '@/fetch/core/util';
import { HttpStatusCode } from '@/fetch/core/constant';
import { translatorEnum } from '@/utils/util';
import './index.scss';

const { TextArea } = Input;

const CreateFollowupGroup = ({ issueInfo }: { issueInfo: any }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [attachmentList, setAttachmentList] = useState<UploadFile[]>([]);
  const [previewImage, setPreviewImage] = useState<any>(null);
  const [userOptions, setUserOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [searchTag, setSearchTag] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [isGroupNameManuallyEdited, setIsGroupNameManuallyEdited] =
    useState(false);
  const assignee = form.getFieldsValue().assignee;

  useEffect(() => {
    if (isModalVisible && !isGroupNameManuallyEdited) {
      if (issueInfo) {
        let eventPart = '';
        if (issueInfo.manualAlarmFeedbackType) {
          eventPart = translatorEnum(
            'ISSUE_FEEDBACK_TYPE_ENUM',
            issueInfo.manualAlarmFeedbackType
          );
        } else if (
          issueInfo.issueEventList &&
          issueInfo.issueEventList.length > 0
        ) {
          eventPart = issueInfo.issueEventList
            .map((e: any) => translatorEnum('ALARM_EVENT_ENUM', e.eventType))
            .join(',');
        }
        if (eventPart.length > 15) eventPart = eventPart.substring(0, 15);
        const nowName = form.getFieldsValue()?.groupName?.split(' @')[0];
        form.setFieldsValue({
          groupName: assignee
            ? nowName
              ? `${nowName} @${assignee}`
              : `${eventPart}-${issueInfo.vehicleName} @${assignee}`
            : `${eventPart}-${issueInfo.vehicleName}`,
        });
      }
    }
  }, [isModalVisible, issueInfo, assignee, isGroupNameManuallyEdited]);

  useEffect(() => {
    issueInfo?.issueStartTime &&
      form.setFieldsValue({
        occurrenceTime: dayjs(issueInfo.issueStartTime),
      });
  }, [issueInfo?.issueStartTime]);

  useEffect(() => {
    form &&
      form.setFieldsValue({
        abnormalType: 'SOFTWARE',
      });
  }, [form]);
  const fetchUsersOption = async () => {
    const res = await request({
      path: '/k2/management/common/get_user_info_list',
      method: 'POST',
    });
    if (res.code === HttpStatusCode.Success) {
      setUserOptions(
        res.data.map((v: any) => ({
          label: `${v.userName}/${v.realName}`,
          value: v.erp,
        }))
      );
    }
  };

  const fetchTagList = async () => {
    try {
      const res = await request({
        path: '/monitor/web/abnormal_followup/get_tag_list',
      });
      if (res.code === HttpStatusCode.Success && res.data) {
        const options = res.data.map((tag: any) => ({
          label: tag.tagName,
          value: tag.tagId,
        }));
        setTagOptions(options);
      }
    } catch (error) {
      console.error('Failed to fetch tag list', error);
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
    fetchTagList();
    fetchUsersOption();
  };
  const handleCancel = () => setIsModalVisible(false);

  const uploadToJiraBucket = async (file: File, fileKey: string) => {
    const preSignRes = await request({
      path: `${location.protocol}//${process.env.JDX_APP_CLOUD_FETCH_DOMAIN}/infrastructure/oss/getPreUrl`,
      method: Method.POST,
      body: {
        bucketName: 'rover-jira',
        fileKey: fileKey,
      },
      headers: { LOPDN: process.env.JDX_APP_REQUEST_HEADER! },
    });

    if (preSignRes.code !== HttpStatusCode.Success || !preSignRes.data) {
      throw new Error('获取Jira上传地址失败');
    }

    const { uploadUrl } = preSignRes.data;

    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error('上传到Jira存储空间失败'));
        }
      };
      xhr.onerror = () => {
        reject(new Error('上传到Jira存储空间失败'));
      };
      xhr.send(file);
    });
  };

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        setLoading(true);
        try {
          if (values.createJira) {
            const jiraUploadPromises = attachmentList
              .filter(
                (file) =>
                  file.status === 'done' &&
                  file.originFileObj &&
                  file.response?.fileKey
              )
              .map((file) => {
                const key = file.response.fileKey;
                return uploadToJiraBucket(file.originFileObj as File, key);
              });
            await Promise.all(jiraUploadPromises);
          }

          const uploadedAttachments = attachmentList
            .filter(
              (file) =>
                file.status === 'done' &&
                file.type === 'image/png' &&
                file.response
            )
            .map((file) => ({ fileKey: file.response.fileKey, type: 'image' }));

          const requestBody = {
            issueNumber: issueInfo.issueNumber,
            vehicleName: issueInfo.vehicleName,
            groupName: values.groupName,
            assignee: values.assignee,
            relatedPersonList: values.relatedPersons || [],
            description: values.description,
            tagIdList: values.issueTags || [],
            occurrenceTime: values.occurrenceTime.format('YYYY-MM-DD HH:mm:ss'),
            attachmentList: uploadedAttachments,
            abnormalType: values.abnormalType,
            affectOperation: values.affectOperation,
            createJira: values.createJira,
            feedbackNumber: issueInfo.manualAlarmFeedbackNumber || '',
          };

          const res = await request({
            path: '/monitor/web/abnormal_followup/create_group',
            method: Method.POST,
            body: requestBody,
          });

          if (res.code === HttpStatusCode.Success) {
            message.success('跟进群创建成功');
            setIsModalVisible(false);
          } else {
            message.error(res.message || '创建失败');
          }
        } catch (error: any) {
          message.error(error.message || '请求异常');
        } finally {
          setLoading(false);
        }
      })
      .catch((info) => console.log('Validate Failed:', info));
  };

  const customUploadRequest = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    try {
      const preSignRes = await request({
        path: `${location.protocol}//${process.env.JDX_APP_CLOUD_FETCH_DOMAIN}/infrastructure/oss/getPreUrl`,
        method: Method.POST,
        body: {
          bucketName: 'rover-operation',
          fileKey: `${Date.now()}-${file.name}`,
        },
        headers: { LOPDN: process.env.JDX_APP_REQUEST_HEADER! },
      });
      if (preSignRes.code !== HttpStatusCode.Success || !preSignRes.data)
        throw new Error('获取上传地址失败');
      const { uploadUrl, fileKey } = preSignRes.data;
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable)
          onProgress(
            { percent: Math.round((event.loaded / event.total) * 100) },
            file
          );
      };
      xhr.onload = () => {
        if (xhr.status === 200) {
          onSuccess({ fileKey }, file);
        } else {
          onError(new Error('上传失败'));
          message.error(`${file.name} 上传失败`);
        }
      };
      xhr.onerror = () => {
        onError(new Error('上传失败'));
        message.error(`${file.name} 上传失败`);
      };
      xhr.send(file);
    } catch (error: any) {
      onError(error);
      message.error(error.message || '上传准备失败');
    }
  };

  const handlePreview = async (file: UploadFile) =>
    setPreviewImage(file.url || (file.preview as string));

  const beforeUpload = (file: UploadFile) => {
    const isLt50M = (file.size || 0) / 1024 / 1024 < 50;
    if (!isLt50M) message.error('图片必须小于50MB!');
    return isLt50M || Upload.LIST_IGNORE;
  };

  const handleCreateTag = async () => {
    setIsCreatingTag(true);
    try {
      if (!searchTag || isCreatingTag) return;
      const res = await request({
        path: '/monitor/web/abnormal_followup/add_tag',
        method: Method.POST,
        body: { tagName: searchTag },
      });
      if (res.code === HttpStatusCode.Success && res.data) {
        const newTag = { label: res.data.tagName, value: res.data.tagId };
        setTagOptions((prev) => [...prev, newTag]);
        fetchTagList();
        form.setFieldsValue({
          issueTags: [...(form.getFieldValue('issueTags') || []), newTag.value],
        });
        setSearchTag('');
      } else {
        message.error(res.message || '创建标签失败');
      }
    } catch (error: any) {
      message.error(error.message || '创建标签请求失败');
    } finally {
      setIsCreatingTag(false);
    }
  };

  const showCreateOption = useMemo(() => {
    if (!searchTag) return false;
    return !tagOptions.some(
      (option) => option.label.toLowerCase() === searchTag.toLowerCase()
    );
  }, [searchTag, tagOptions]);

  return (
    <>
      <a className="accident-btn" onClick={showModal}>
        创建跟进群
      </a>
      {isModalVisible && (
        <Modal
          title="创建跟进群"
          className="create-followup-group"
          open={isModalVisible}
          maskClosable={false}
          destroyOnHidden
          confirmLoading={loading}
          width={800}
          onOk={handleOk}
          onCancel={handleCancel}
          afterClose={() => {
            form.resetFields();
            setAttachmentList([]);
            setSearchTag('');
          }}
        >
          <Form
            form={form}
            layout="horizontal"
            colon={false}
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 14 }}
            name="create-followup-group-form"
          >
            <Form.Item
              name="groupName"
              label="群名称"
              rules={[
                { required: true, message: '请输入群名称' },
                { max: 40, message: '群名称不能超过40个字符' },
              ]}
            >
              <Input
                maxLength={40}
                showCount
                onChange={() => setIsGroupNameManuallyEdited(true)}
              />
            </Form.Item>
            <Form.Item
              name="assignee"
              label="受理人"
              rules={[{ required: true, message: '请选择受理人' }]}
            >
              <Select
                showSearch
                placeholder="搜索用户名/ERP"
                filterOption={(inputValue, option: any) => {
                  return String(option?.label)?.includes(inputValue);
                }}
                options={userOptions}
              />
            </Form.Item>
            <Form.Item name="relatedPersons" label="相关人">
              <Select
                mode="multiple"
                showSearch
                placeholder="搜索用户名/ERP"
                filterOption={(inputValue, option: any) => {
                  return String(option?.label)?.includes(inputValue);
                }}
                options={userOptions}
              />
            </Form.Item>
            <Form.Item
              name="description"
              label="问题描述"
              rules={[
                { required: true, message: '请输入问题描述' },
                { max: 30, message: '问题描述不能超过30个字符' },
              ]}
            >
              <TextArea
                rows={3}
                maxLength={30}
                showCount
                placeholder="请输入问题描述"
              />
            </Form.Item>
            <Form.Item name="issueTags" label="问题标签">
              <Select
                mode="multiple"
                showSearch
                placeholder="搜索或创建标签"
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                searchValue={searchTag}
                options={tagOptions}
                popupRender={(menu) => (
                  <>
                    {showCreateOption && (
                      <>
                        <div
                          style={{ padding: '8px', cursor: 'pointer' }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleCreateTag}
                        >
                          {`[+] 创建新标签: '${searchTag}'`}
                        </div>
                        <Divider style={{ margin: '4px 0' }} />
                      </>
                    )}
                    {menu}
                  </>
                )}
                onSearch={setSearchTag}
              />
            </Form.Item>
            <Form.Item
              name="occurrenceTime"
              label="发生时间"
              rules={[{ required: true, message: '请选择发生时间' }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                locale={locale}
              />
            </Form.Item>
            <Form.Item
              name="attachmentList"
              label="上传图片"
              tooltip="复制文件后，在页面任意位置粘贴即可完成上传。"
            >
              <>
                <Upload
                  listType="picture-card"
                  fileList={attachmentList}
                  maxCount={4}
                  pastable={true}
                  onPreview={handlePreview}
                  beforeUpload={beforeUpload}
                  onChange={({ fileList: newFileList }) => {
                    setAttachmentList(newFileList);
                  }}
                  customRequest={customUploadRequest}
                >
                  {attachmentList.length >= 4 ? null : (
                    <button
                      style={{ border: 0, background: 'none', color: 'white' }}
                      type="button"
                    >
                      <UploadOutlined />
                      <div style={{ marginTop: 6, fontSize: '14px' }}>
                        粘贴或点击上传
                      </div>
                    </button>
                  )}
                </Upload>
                {previewImage && (
                  <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                      visible: previewImage ? true : false,
                      onVisibleChange: (visible) => setPreviewImage(null),
                      afterOpenChange: (visible) =>
                        !visible && setPreviewImage(null),
                    }}
                    src={previewImage}
                  />
                )}
              </>
            </Form.Item>
            <Form.Item
              name="abnormalType"
              label="问题类型"
              rules={[{ required: true, message: '请选择问题类型' }]}
            >
              <Radio.Group>
                <Radio value="SOFTWARE">软件</Radio>
                <Radio value="HARDWARE">硬件</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="affectOperation"
              label="是否影响运营"
              rules={[{ required: true, message: '请选择是否影响运营' }]}
            >
              <Radio.Group>
                <Radio value={true}>是</Radio>
                <Radio value={false}>否</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="createJira"
              label="是否同步创建缺陷"
              rules={[{ required: true, message: '请选择是否同步创建缺陷' }]}
            >
              <Radio.Group>
                <Radio value={true}>是</Radio>
                <Radio value={false}>否</Radio>
              </Radio.Group>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default CreateFollowupGroup;
